/**
 * Products Management Page
 *
 * Dedicated screen for managing products and product listings.
 * Includes product list, details, inventory management, and custom domain settings.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api';
import type { ProductSettings } from '../types/form';

interface Product {
  id: string;
  name: string;
  slug: string;
  description?: string;
  status: 'draft' | 'published' | 'archived';
  created_at: string;
  updated_at: string;
  schema?: {
    settings?: {
      product?: ProductSettings;
    };
  };
}

export default function ProductsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'inventory' | 'images' | 'variants' | 'domain'>('details');

  // Product settings
  const [productSettings, setProductSettings] = useState<ProductSettings>({
    enabled: true,
    currency: 'USD',
    trackInventory: false,
    allowBackorders: false,
    requiresShipping: true,
    isActive: true,
  });

  // Load product data
  useEffect(() => {
    async function loadProduct() {
      if (!id) return;

      try {
        setLoading(true);
        const response = await api.get(`/api/forms/${id}`);
        const data = await response.json() as Product;
        setProduct(data);

        if (data.schema?.settings?.product) {
          setProductSettings(data.schema.settings.product);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load product');
      } finally {
        setLoading(false);
      }
    }

    loadProduct();
  }, [id]);

  // Save product settings
  const handleSave = useCallback(async () => {
    if (!id || !product) return;

    try {
      setSaving(true);
      await api.put(`/api/forms/${id}`, {
        schema: {
          ...product.schema,
          settings: {
            ...product.schema?.settings,
            product: productSettings,
          },
        },
      });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save product');
    } finally {
      setSaving(false);
    }
  }, [id, product, productSettings]);

  // Publish product
  const handlePublish = useCallback(async () => {
    if (!id) return;

    try {
      setSaving(true);
      await api.post(`/api/forms/${id}/publish`);
      setProduct(prev => prev ? { ...prev, status: 'published' } : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish product');
    } finally {
      setSaving(false);
    }
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (error && !product) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 dark:text-red-400">{error}</p>
        <button
          onClick={() => navigate('/formspace')}
          className="mt-4 text-teal-600 hover:underline"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Product not found</p>
        <button
          onClick={() => navigate('/formspace')}
          className="mt-4 text-teal-600 hover:underline"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/formspace"
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center">
                  <svg className="w-5 h-5 text-teal-600 dark:text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{product.name}</h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Product Page
                    {product.status === 'published' && (
                      <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400 rounded-full">
                        Published
                      </span>
                    )}
                    {product.status === 'draft' && (
                      <span className="ml-2 px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-400 rounded-full">
                        Draft
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Draft'}
            </button>
            <button
              onClick={handlePublish}
              disabled={saving || product.status === 'published'}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              {product.status === 'published' ? 'Published' : 'Publish'}
            </button>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex gap-4">
          {(['details', 'inventory', 'images', 'variants', 'domain'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className="p-6 space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Product Details</h3>

              {/* Pricing */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Currency
                  </label>
                  <select
                    value={productSettings.currency}
                    onChange={(e) => setProductSettings({ ...productSettings, currency: e.target.value })}
                    className="w-full input"
                  >
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="ILS">ILS</option>
                    <option value="JPY">JPY</option>
                    <option value="CAD">CAD</option>
                    <option value="AUD">AUD</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Price
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={productSettings.price || ''}
                    onChange={(e) => setProductSettings({ ...productSettings, price: parseFloat(e.target.value) || undefined })}
                    className="w-full input"
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Compare at price */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Compare at Price (Original)
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={productSettings.compareAtPrice || ''}
                    onChange={(e) => setProductSettings({ ...productSettings, compareAtPrice: parseFloat(e.target.value) || undefined })}
                    className="w-full input"
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">Show a strikethrough price to indicate a sale</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    SKU
                  </label>
                  <input
                    type="text"
                    value={productSettings.sku || ''}
                    onChange={(e) => setProductSettings({ ...productSettings, sku: e.target.value || undefined })}
                    className="w-full input"
                    placeholder="SKU-001"
                  />
                </div>
              </div>

              {/* Category & Tags */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category
                  </label>
                  <input
                    type="text"
                    value={productSettings.category || ''}
                    onChange={(e) => setProductSettings({ ...productSettings, category: e.target.value || undefined })}
                    className="w-full input"
                    placeholder="e.g., Electronics, Clothing"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tags (comma separated)
                  </label>
                  <input
                    type="text"
                    value={productSettings.tags?.join(', ') || ''}
                    onChange={(e) => setProductSettings({ ...productSettings, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                    className="w-full input"
                    placeholder="sale, featured, new"
                  />
                </div>
              </div>

              {/* SEO */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">SEO Settings</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      SEO Title
                    </label>
                    <input
                      type="text"
                      value={productSettings.seoTitle || ''}
                      onChange={(e) => setProductSettings({ ...productSettings, seoTitle: e.target.value || undefined })}
                      className="w-full input"
                      placeholder="Product title for search engines"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      SEO Description
                    </label>
                    <textarea
                      value={productSettings.seoDescription || ''}
                      onChange={(e) => setProductSettings({ ...productSettings, seoDescription: e.target.value || undefined })}
                      className="w-full input"
                      rows={3}
                      placeholder="Description for search engines (150-160 characters recommended)"
                    />
                  </div>
                </div>
              </div>

              {/* Shipping */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Shipping</h4>
                <label className="flex items-center gap-3 mb-4">
                  <input
                    type="checkbox"
                    checked={productSettings.requiresShipping}
                    onChange={(e) => setProductSettings({ ...productSettings, requiresShipping: e.target.checked })}
                    className="rounded border-gray-300 dark:border-gray-600 text-teal-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">This is a physical product that requires shipping</span>
                </label>

                {productSettings.requiresShipping && (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Weight
                      </label>
                      <input
                        type="number"
                        min={0}
                        step={0.01}
                        value={productSettings.weight || ''}
                        onChange={(e) => setProductSettings({ ...productSettings, weight: parseFloat(e.target.value) || undefined })}
                        className="w-full input"
                        placeholder="0.0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Weight Unit
                      </label>
                      <select
                        value={productSettings.weightUnit || 'kg'}
                        onChange={(e) => setProductSettings({ ...productSettings, weightUnit: e.target.value as 'kg' | 'lb' | 'oz' | 'g' })}
                        className="w-full input"
                      >
                        <option value="kg">kg</option>
                        <option value="lb">lb</option>
                        <option value="oz">oz</option>
                        <option value="g">g</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Inventory Tab */}
        {activeTab === 'inventory' && (
          <div className="p-6 space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Inventory Management</h3>

            <label className="flex items-center gap-3 mb-6">
              <input
                type="checkbox"
                checked={productSettings.trackInventory}
                onChange={(e) => setProductSettings({ ...productSettings, trackInventory: e.target.checked })}
                className="rounded border-gray-300 dark:border-gray-600 text-teal-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Track inventory quantity</span>
            </label>

            {productSettings.trackInventory && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Current Quantity
                    </label>
                    <input
                      type="number"
                      min={0}
                      value={productSettings.inventoryQuantity || 0}
                      onChange={(e) => setProductSettings({ ...productSettings, inventoryQuantity: parseInt(e.target.value) || 0 })}
                      className="w-full input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Low Stock Alert Threshold
                    </label>
                    <input
                      type="number"
                      min={0}
                      value={productSettings.lowStockThreshold || 5}
                      onChange={(e) => setProductSettings({ ...productSettings, lowStockThreshold: parseInt(e.target.value) || 5 })}
                      className="w-full input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Get notified when stock falls below this level</p>
                  </div>
                </div>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={productSettings.allowBackorders}
                    onChange={(e) => setProductSettings({ ...productSettings, allowBackorders: e.target.checked })}
                    className="rounded border-gray-300 dark:border-gray-600 text-teal-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Allow backorders when out of stock</span>
                </label>

                {/* Inventory Status */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Current Status</h4>
                  <div className="flex items-center gap-4">
                    {(productSettings.inventoryQuantity || 0) > (productSettings.lowStockThreshold || 5) ? (
                      <span className="flex items-center gap-2 text-green-600 dark:text-green-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        In Stock
                      </span>
                    ) : (productSettings.inventoryQuantity || 0) > 0 ? (
                      <span className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        Low Stock
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 text-red-600 dark:text-red-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Out of Stock
                      </span>
                    )}
                    <span className="text-gray-500 dark:text-gray-400">
                      {productSettings.inventoryQuantity || 0} units available
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Images Tab */}
        {activeTab === 'images' && (
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Product Images</h3>

            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
              <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                Drag and drop images here, or click to browse
              </p>
              <button className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700">
                Upload Images
              </button>
              <p className="text-xs text-gray-400 mt-2">
                Supports: JPG, PNG, WebP. Max size: 5MB
              </p>
            </div>

            {productSettings.images && productSettings.images.length > 0 && (
              <div className="mt-6 grid grid-cols-4 gap-4">
                {productSettings.images.map((image, index) => (
                  <div key={image.id} className="relative group">
                    <img
                      src={image.url}
                      alt={image.alt || `Product image ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <button className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Variants Tab */}
        {activeTab === 'variants' && (
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Product Variants</h3>

            <label className="flex items-center gap-3 mb-6">
              <input
                type="checkbox"
                checked={productSettings.hasVariants}
                onChange={(e) => setProductSettings({ ...productSettings, hasVariants: e.target.checked })}
                className="rounded border-gray-300 dark:border-gray-600 text-teal-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">This product has multiple variants (e.g., sizes, colors)</span>
            </label>

            {productSettings.hasVariants && (
              <div className="space-y-6">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Add variants to offer different options like sizes, colors, or materials. Each variant can have its own price and inventory.
                  </p>
                </div>

                <button className="px-4 py-2 border border-teal-500 text-teal-600 dark:text-teal-400 rounded-lg hover:bg-teal-50 dark:hover:bg-teal-900/20">
                  + Add Variant
                </button>

                {productSettings.variants && productSettings.variants.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Variant</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">SKU</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Price</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Quantity</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productSettings.variants.map((variant) => (
                          <tr key={variant.id} className="border-b border-gray-100 dark:border-gray-700">
                            <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">{variant.name}</td>
                            <td className="py-3 px-4 text-sm text-gray-500 dark:text-gray-400">{variant.sku || '-'}</td>
                            <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">{productSettings.currency} {variant.price.toFixed(2)}</td>
                            <td className="py-3 px-4 text-sm text-gray-500 dark:text-gray-400">{variant.inventoryQuantity || '-'}</td>
                            <td className="py-3 px-4">
                              <button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Domain Tab */}
        {activeTab === 'domain' && (
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Custom Domain</h3>

            <div className="space-y-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300">Connect Your Domain</h4>
                    <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                      Display this product on your own domain by adding a CNAME record pointing to our servers.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Custom Domain
                </label>
                <input
                  type="text"
                  value={productSettings.customDomain || ''}
                  onChange={(e) => setProductSettings({ ...productSettings, customDomain: e.target.value || undefined })}
                  className="w-full input"
                  placeholder="shop.yourdomain.com"
                />
              </div>

              {productSettings.customDomain && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">DNS Configuration</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Add the following CNAME record to your domain's DNS settings:
                  </p>
                  <div className="bg-gray-900 text-gray-100 rounded-lg p-4 font-mono text-sm">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-400">Type:</span>
                      <span>CNAME</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-400">Name:</span>
                      <span>{productSettings.customDomain?.split('.')[0] || 'shop'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Value:</span>
                      <span>products.xtform.app</span>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center gap-2">
                    {productSettings.cnameVerified ? (
                      <span className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Domain verified
                      </span>
                    ) : (
                      <>
                        <span className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 text-sm">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Pending verification
                        </span>
                        <button className="text-sm text-teal-600 hover:underline">
                          Verify now
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Product URL</h4>
                <div className="flex items-center gap-2">
                  <code className="bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded text-sm text-gray-800 dark:text-gray-200 flex-1">
                    {window.location.origin}/product/{product.slug}
                  </code>
                  <button
                    onClick={() => navigator.clipboard.writeText(`${window.location.origin}/product/${product.slug}`)}
                    className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    title="Copy URL"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
